<template>
    <div>
        <card-header :title="'Transaction #' + (transaction.id || '')" icon="fa-usd-circle" />

        <card-toolbar v-if="transaction.intent_id || transaction.receipt_url">
            <button @click="openReceipt" v-if="transaction.intent_id || transaction.receipt_url">
                <i class="fas fa-receipt"></i>
                View Stripe Receipt
            </button>
        </card-toolbar>

        <card-body>
            <card-list>
                <div v-if="loading" class="has-text-centered py-5">
                    <b-loading :is-full-page="false" v-model="loading" :can-cancel="false"></b-loading>
                </div>

                <template v-else>
                    <!-- Basic Info -->
                    <subheader-list-item icon="fa-info-circle" title="Transaction Details"/>
                    <container-list-item>
                        <data-row title="ID">{{ transaction.id }}</data-row>
                        <data-row title="Type">{{ transaction.type_label }}</data-row>
                        <data-row title="Created">{{ transaction.created_at | moment('MMMM Do YYYY, h:mm:ss a') }}</data-row>
                        <data-row title="Completed" v-if="transaction.completed_at">{{ transaction.completed_at | moment('MMMM Do YYYY, h:mm:ss a') }}</data-row>
                        <data-row title="Intent ID" v-if="transaction.intent_id">{{ transaction.intent_id }}</data-row>
                        <data-row title="Charge ID" v-if="transaction.charge_id">{{ transaction.charge_id }}</data-row>
                        <data-row title="Type ID" v-if="transaction.type_id">{{ transaction.type_id }}</data-row>
                    </container-list-item>

                    <!-- Amount Info -->
                    <subheader-list-item icon="fa-dollar-sign" title="Amount"/>
                    <container-list-item>
                        <!-- For plan_taxes: tax_amount is what was paid, base_amount is the plan total -->
                        <template v-if="transaction.type === 'plan_taxes'">
                            <data-row title="Plan Total">{{ formatCurrency(transaction.base_amount) }}</data-row>
                            <data-row title="Tax Paid">
                                <strong>{{ formatCurrency(transaction.tax_amount) }}</strong>
                            </data-row>
                        </template>
                        <template v-else>
                            <data-row title="Base Amount">{{ formatCurrency(transaction.base_amount) }}</data-row>
                            <data-row title="Tax Amount">{{ formatCurrency(transaction.tax_amount) }}</data-row>
                            <data-row title="Total Amount">
                                <strong>{{ formatCurrency(transaction.total_amount) }}</strong>
                            </data-row>
                        </template>
                    </container-list-item>

                    <!-- Tax Info -->
                    <subheader-list-item icon="fa-percentage" title="Tax Details" v-if="transaction.tax_rate"/>
                    <container-list-item v-if="transaction.tax_rate">
                        <data-row title="Tax Rate">{{ formatPercent(transaction.tax_rate) }}</data-row>
                        <data-row title="State Rate" v-if="transaction.tax_state_rate">{{ formatPercent(transaction.tax_state_rate) }}</data-row>
                        <data-row title="Local Rate" v-if="transaction.tax_local_rate">{{ formatPercent(transaction.tax_local_rate) }}</data-row>
                        <data-row title="Jurisdiction">{{ transaction.tax_jurisdiction || 'N/A' }}</data-row>
                        <data-row title="Location Code">{{ transaction.tax_location_code || 'N/A' }}</data-row>
                    </container-list-item>

                    <!-- User Tile -->
                    <subheader-list-item icon="fa-user" title="User" v-if="transaction.user"/>
                    <user-tile
                        v-if="transaction.user"
                        :card="card"
                        :user="transaction.user"
                        @click="openUser"/>

                    <!-- Signature (for reseller_cart) -->
                    <subheader-list-item icon="fa-signature" title="Signature" v-if="transaction.signature_img"/>
                    <container-list-item padded v-if="transaction.signature_img">
                        <div class="signature-container">
                            <img :src="transaction.signature_img" alt="Signature" class="signature-image"/>
                        </div>
                        <div class="signature-clause mt-3" v-if="transaction.signature_clause">
                            <small class="has-text-grey">{{ transaction.signature_clause }}</small>
                        </div>
                        <div class="mt-2" v-if="transaction.signature_hash">
                            <small class="has-text-grey-light">Hash: {{ transaction.signature_hash }}</small>
                        </div>
                    </container-list-item>
                </template>
            </card-list>
        </card-body>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardBody from "@/TIER/components/CardBody";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import DataRow from "@/TIER/components/DataRow";
    import UserTile from "@/components/tiles/UserTile";
    import {client as http} from "@/http_client";
    import numeral from "numeral";

    export default {
        components: {
            CardHeader,
            CardToolbar,
            CardBody,
            CardList,
            SubheaderListItem,
            ContainerListItem,
            DataRow,
            UserTile
        },
        props: ['card', 'props'],
        data() {
            return {
                transaction: {},
                loading: true
            }
        },
        mounted() {
            this.loadTransaction();
        },
        watch: {
            'props.transactionId': function() {
                this.loadTransaction();
            }
        },
        methods: {
            reload() {
                this.loadTransaction();
            },

            loadTransaction() {
                this.$emit('loading', true);
                this.loading = true;

                http.get('transactions/' + this.props.transactionId).then(response => {
                    this.transaction = response.data;
                    this.loading = false;
                    this.$emit('loading', false);
                }).catch(err => {
                    console.error('Error loading transaction:', err);
                    this.$buefy.toast.open({
                        message: 'Failed to load transaction details',
                        type: 'is-danger'
                    });
                    this.loading = false;
                    this.$emit('loading', false);
                });
            },

            formatCurrency(value) {
                return numeral(value).format('$0,0.00');
            },

            formatPercent(value) {
                return numeral(value * 100).format('0.000') + '%';
            },

            openReceipt() {
                if (this.transaction.receipt_url) {
                    window.open(this.transaction.receipt_url, '_blank');
                } else if (this.transaction.intent_id) {
                    // Fetch receipt URL from Stripe via portal endpoint
                    http.get('stripe/receipt/' + this.transaction.intent_id).then(response => {
                        if (response.data.url) {
                            window.open(response.data.url, '_blank');
                        } else {
                            this.$buefy.toast.open({
                                message: 'Receipt URL not available',
                                type: 'is-warning'
                            });
                        }
                    }).catch(err => {
                        console.error('Error fetching receipt:', err);
                        this.$buefy.toast.open({
                            message: 'Failed to fetch receipt URL',
                            type: 'is-danger'
                        });
                    });
                }
            },

            openUser() {
                if (this.transaction.user && this.transaction.user.id) {
                    this.$openCard('user', {userId: this.transaction.user.id}, this.card);
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .signature-container {
        background: #f5f5f5;
        padding: 1rem;
        border-radius: 4px;
        text-align: center;

        .signature-image {
            max-width: 100%;
            max-height: 150px;
            border: 1px solid #ddd;
            background: white;
        }
    }

    .signature-clause {
        padding: 0.5rem;
        background: #fafafa;
        border-radius: 4px;
        font-style: italic;
    }
</style>
